import React, { useState } from "react";
import { toast } from "react-toastify";
import { FaWhatsapp, FaRegCopy } from "react-icons/fa";
import { apiGetOrderVendorHandoff } from "../../api/index";

/**
 * "Copy order" and "Send on WhatsApp" for an order being coordinated with a
 * vendor or outlet manager by hand (17 Oct 2026).
 *
 * The message is composed by the SERVER (GET /weborders/:id/vendor-handoff,
 * services/orders/orderVendorHandoff.js), which also picks the number: the
 * fulfilling outlet's phone, else the vendor profile's, else the owner's. This
 * component only fetches, copies and opens; it never assembles order text from
 * the page, so every order page sends the same words.
 *
 * Both buttons are filled (dashboard rule: no outlined buttons); the action
 * colour is the standard #083860, WhatsApp keeps its own green so the
 * destination is unmistakable.
 */
const base = {
  border: "none",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 600,
  padding: "10px 14px",
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
};

export default function VendorHandoffActions({ orderId, style }) {
  const [busy, setBusy] = useState(null); // "copy" | "whatsapp" | null

  const load = async () => {
    const { data } = await apiGetOrderVendorHandoff(orderId);
    if (!data?.success || !data?.data?.text) throw new Error(data?.message || "Could not prepare the order");
    return data.data;
  };

  const copy = async () => {
    if (!orderId || busy) return;
    setBusy("copy");
    try {
      const { text } = await load();
      await navigator.clipboard.writeText(text);
      toast.success("Order copied. Paste it anywhere.");
    } catch (err) {
      toast.error(err?.message || "Could not copy the order.");
    } finally {
      setBusy(null);
    }
  };

  const whatsapp = async () => {
    if (!orderId || busy) return;
    setBusy("whatsapp");
    // Open the window inside the click so the browser does not treat it as a
    // popup; point it at the conversation once the server has answered.
    const win = window.open("", "_blank", "noopener");
    try {
      const { whatsappUrl, contact, text } = await load();
      if (whatsappUrl) {
        if (win) win.location.href = whatsappUrl;
        else window.open(whatsappUrl, "_blank", "noopener");
        toast.info(`Opening WhatsApp to ${contact?.label || "the vendor"}.`);
      } else {
        if (win) win.close();
        await navigator.clipboard.writeText(text);
        toast.warning("No WhatsApp number is saved for this vendor. The order text is copied; paste it into the chat yourself.");
      }
    } catch (err) {
      if (win) win.close();
      toast.error(err?.message || "Could not open WhatsApp.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", ...style }}>
      <button type="button" onClick={copy} disabled={!!busy} style={{ ...base, backgroundColor: "#083860", opacity: busy ? 0.7 : 1 }} title="Copy the order as a message">
        <FaRegCopy /> {busy === "copy" ? "Copying" : "Copy order"}
      </button>
      <button type="button" onClick={whatsapp} disabled={!!busy} style={{ ...base, backgroundColor: "#128C7E", opacity: busy ? 0.7 : 1 }} title="Send the order to the vendor on WhatsApp">
        <FaWhatsapp /> {busy === "whatsapp" ? "Opening" : "Send on WhatsApp"}
      </button>
    </div>
  );
}
